import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../supabaseClient'
import { useAuth } from '../../contexts/AuthContext'
import ImageUploader from '../../components/ImageUploader'

/**
 * SubmitCompleteLurePackage Component
 * 
 * Allows users to submit a complete lure package in one form:
 * - Brand (new or existing)
 * - Base Lure details
 * - Color information
 * - Variant information
 * - Product images (optional)
 * 
 * This is much more convenient than submitting each piece separately.
 * Daily limit: 10 complete packages per day
 */
function SubmitCompleteLurePackage() {
  const { user } = useAuth()
  const navigate = useNavigate()

  // ============================================
  // STATE MANAGEMENT
  // ============================================
  
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [remainingSubmissions, setRemainingSubmissions] = useState(10) // Default to 10
  
  // Brand selection
  const [useExistingBrand, setUseExistingBrand] = useState(true) // Default to existing
  const [brands, setBrands] = useState([])
  const [types, setTypes] = useState([])
  const [subtypes, setSubtypes] = useState([])
  
  // LURE DETECTION STATE (NEW)
  const [existingLure, setExistingLure] = useState(null)
  const [showLureWarning, setShowLureWarning] = useState(false)
  
  // Form data
  const [formData, setFormData] = useState({
    // Brand fields
    existingBrandId: '',
    newBrandName: '',
    newBrandCountry: '',
    newBrandWebsite: '',
    newBrandDescription: '',
    
    // Lure fields
    lureName: '',
    lureTypeId: '',
    lureSubtypeId: '',
    lureWeight: '',
    lureLength: '',
    lureDepthRange: '',
    lureDescription: '',
    
    // Color fields
    colorName: '',
    colorDescription: '',
    isLimitedEdition: false,
    isDiscontinued: false,
    
    // Variant fields
    variantName: '', // Optional - defaults to color name if empty
    variantNotes: ''
  })

  // Image upload state
  const [submissionImages, setSubmissionImages] = useState([])
  const [imageMetadata, setImageMetadata] = useState({ primaryIndex: 0, captions: {} })
  const [uploadingImages, setUploadingImages] = useState(false)

  // ============================================
  // LOAD INITIAL DATA
  // ============================================
  
  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }
    
    fetchInitialData()
  }, [user, navigate])

  // Clear opposite fields when switching brand type
  useEffect(() => {
    if (useExistingBrand) {
      setFormData(prev => ({
        ...prev,
        newBrandName: '',
        newBrandCountry: '',
        newBrandWebsite: '',
        newBrandDescription: ''
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        existingBrandId: ''
      }))
    }
    
    // Reset lure detection when brand type changes
    setExistingLure(null)
    setShowLureWarning(false)
  }, [useExistingBrand])

  // REAL-TIME LURE DETECTION (NEW)
  useEffect(() => {
    const detectExistingLure = async () => {
      // Only check if:
      // 1. Using existing brand
      // 2. Brand is selected
      // 3. Lure name has at least 2 characters
      if (!useExistingBrand || !formData.existingBrandId || formData.lureName.length < 2) {
        setExistingLure(null)
        setShowLureWarning(false)
        return
      }

      try {
        // Search for existing lure with this brand + name
        const { data, error } = await supabase
          .from('base_lures')
          .select(`
            base_lure_id,
            name,
            type_id,
            subtype_id,
            weight,
            length,
            depth_range,
            description,
            image_url,
            lure_types (type_id, type_name),
            lure_subtypes (subtype_id, subtype_name)
          `)
          .eq('brand_id', formData.existingBrandId)
          .ilike('name', formData.lureName.trim())
          .limit(1)
          .maybeSingle()
        
        if (error && error.code !== 'PGRST116') {
          console.error('Error checking for existing lure:', error)
          return
        }

        if (data) {
          console.log('🎣 Existing lure found:', data)
          setExistingLure(data)
          setShowLureWarning(true)
          
          // Pre-fill lure details from existing lure (read-only display)
          setFormData(prev => ({
            ...prev,
            lureTypeId: data.type_id?.toString() || prev.lureTypeId,
            lureSubtypeId: data.subtype_id?.toString() || prev.lureSubtypeId,
            lureWeight: data.weight || prev.lureWeight,
            lureLength: data.length || prev.lureLength,
            lureDepthRange: data.depth_range || prev.lureDepthRange
          }))
        } else {
          setExistingLure(null)
          setShowLureWarning(false)
        }
        
      } catch (error) {
        console.error('Error in lure detection:', error)
      }
    }

    // Debounce the search to avoid too many API calls
    const timeoutId = setTimeout(detectExistingLure, 500)
    return () => clearTimeout(timeoutId)
    
  }, [useExistingBrand, formData.existingBrandId, formData.lureName])

  const fetchInitialData = async () => {
    try {
      // Fetch existing brands
      const { data: brandsData } = await supabase
        .from('brands')
        .select('brand_id, brand_name')
        .order('brand_name')
      
      // Fetch lure types
      const { data: typesData } = await supabase
        .from('lure_types')
        .select('type_id, type_name')
        .order('type_name')
      
      // Fetch lure subtypes
      const { data: subtypesData } = await supabase
        .from('lure_subtypes')
        .select('subtype_id, subtype_name')
        .order('subtype_name')
      
      setBrands(brandsData || [])
      setTypes(typesData || [])
      setSubtypes(subtypesData || [])
      
      // Check submission limit
      await fetchSubmissionLimits()
      
      setLoading(false)
    } catch (error) {
      console.error('Error loading data:', error)
      setError('Failed to load form data')
      setLoading(false)
    }
  }

  // ============================================
  // CHECK SUBMISSION LIMITS
  // ============================================
  
  const fetchSubmissionLimits = async () => {
    try {
      const today = new Date().toISOString().split('T')[0]
      
      console.log('=== CHECKING SUBMISSION LIMIT ===')
      console.log('User ID:', user.id)
      console.log('Today:', today)
      
      // Get current count for today
      const { data: limitData, error: queryError } = await supabase
        .from('user_submission_limits')
        .select('complete_lure_count')
        .eq('user_id', user.id)
        .eq('submission_date', today)
        .maybeSingle()
      
      if (queryError && queryError.code !== 'PGRST116') {
        console.error('Query error:', queryError)
        throw queryError
      }
      
      // If no row exists for today, user has 10 submissions remaining
      const currentCount = limitData?.complete_lure_count || 0
      const remaining = Math.max(0, 10 - currentCount)
      
      console.log('Current count:', currentCount)
      console.log('Remaining:', remaining)
      
      setRemainingSubmissions(remaining)
      
    } catch (error) {
      console.error('Error checking submission limits:', error)
      setRemainingSubmissions(0)
    }
  }

  // ============================================
  // FORM HANDLERS
  // ============================================
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleBrandTypeChange = (useExisting) => {
    setUseExistingBrand(useExisting)
  }

  const handleSubmissionImagesChange = (images, metadata) => {
    console.log('📸 Images changed:', images.length, 'images')
    console.log('📸 Metadata:', metadata)
    setSubmissionImages(images)
    setImageMetadata(metadata)
  }

  // ============================================
  // UPLOAD IMAGES TO SUPABASE
  // ============================================

  const uploadImagesToSupabase = async (submissionId) => {
    if (submissionImages.length === 0) {
      console.log('No images to upload')
      return []
    }

    setUploadingImages(true)
    const uploadedUrls = []

    try {
      console.log(`📸 Uploading ${submissionImages.length} images for submission ${submissionId}`)

      for (let i = 0; i < submissionImages.length; i++) {
        const file = submissionImages[i]
        const isPrimary = i === imageMetadata.primaryIndex
        const caption = imageMetadata.captions[i] || ''

        // Generate unique filename
        const fileExt = file.name.split('.').pop()
        const fileName = `${submissionId}_${Date.now()}_${i}.${fileExt}`
        const filePath = `submission-images/${fileName}`

        console.log(`Uploading image ${i + 1}/${submissionImages.length}: ${fileName}`)

        // Upload to Supabase Storage
        const { error: uploadError } = await supabase.storage
          .from('lure-images')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          })

        if (uploadError) {
          console.error(`Error uploading image ${i + 1}:`, uploadError)
          throw uploadError
        }

        // Get public URL
        const { data: publicUrlData } = supabase.storage
          .from('lure-images')
          .getPublicUrl(filePath)

        const imageUrl = publicUrlData.publicUrl

        console.log(`✅ Image ${i + 1} uploaded:`, imageUrl)

        // Store image record in submission_images table
        const { error: dbError } = await supabase
          .from('submission_images')
          .insert({
            submission_id: submissionId,
            image_url: imageUrl,
            is_primary: isPrimary,
            caption: caption || null,
            upload_order: i
          })

        if (dbError) {
          console.error('Error saving image record:', dbError)
          throw dbError
        }

        uploadedUrls.push({
          url: imageUrl,
          isPrimary,
          caption
        })
      }

      console.log(`✅ All ${uploadedUrls.length} images uploaded successfully`)
      return uploadedUrls

    } catch (error) {
      console.error('Error in uploadImagesToSupabase:', error)
      throw error
    } finally {
      setUploadingImages(false)
    }
  }

  // ============================================
  // SUBMIT HANDLER
  // ============================================
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Clear previous messages
    setError('')
    setSuccess(false)
    
    // Check remaining submissions
    if (remainingSubmissions <= 0) {
      setError('You have reached your daily submission limit (10 per day)')
      // Scroll to error banner at bottom
      setTimeout(() => {
        const errorElement = document.querySelector('.contextual-feedback')
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
      return
    }
    
    // Validation
    if (useExistingBrand && !formData.existingBrandId) {
      setError('Please select a brand')
      setTimeout(() => {
        const errorElement = document.querySelector('.contextual-feedback')
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
      return
    }
    
    if (!useExistingBrand && !formData.newBrandName.trim()) {
      setError('Please enter a brand name')
      setTimeout(() => {
        const errorElement = document.querySelector('.contextual-feedback')
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
      return
    }
    
    if (!formData.lureName.trim()) {
      setError('Please enter a lure name')
      setTimeout(() => {
        const errorElement = document.querySelector('.contextual-feedback')
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
      return
    }
    
    if (!formData.lureTypeId) {
      setError('Please select a lure type')
      setTimeout(() => {
        const errorElement = document.querySelector('.contextual-feedback')
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
      return
    }
    
    if (!formData.colorName.trim()) {
      setError('Please enter a color name')
      setTimeout(() => {
        const errorElement = document.querySelector('.contextual-feedback')
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
      return
    }
    
    try {
      setSubmitting(true)
      
      console.log('=== SUBMITTING COMPLETE LURE PACKAGE ===')
      console.log('Form Data:', formData)
      console.log('Use Existing Brand:', useExistingBrand)
      console.log('User ID:', user.id)
      console.log('Images to upload:', submissionImages.length)
      
      // Prepare submission data
      const submissionData = {
        user_id: user.id,
        
        // Brand fields
        use_existing_brand: useExistingBrand,
        existing_brand_id: useExistingBrand ? parseInt(formData.existingBrandId) : null,
        new_brand_name: !useExistingBrand ? formData.newBrandName.trim() : null,
        new_brand_country: !useExistingBrand ? formData.newBrandCountry.trim() || null : null,
        new_brand_website_url: !useExistingBrand ? formData.newBrandWebsite.trim() || null : null,
        new_brand_description: !useExistingBrand ? formData.newBrandDescription.trim() || null : null,
        
        // Existing lure tracking (for backend duplicate detection)
        existing_lure_id: existingLure ? existingLure.base_lure_id : null,
        
        // Lure fields
        lure_name: formData.lureName.trim(),
        lure_type_id: parseInt(formData.lureTypeId),
        lure_subtype_id: formData.lureSubtypeId ? parseInt(formData.lureSubtypeId) : null,
        lure_weight: formData.lureWeight.trim() || null,
        lure_length: formData.lureLength.trim() || null,
        lure_depth_range: formData.lureDepthRange.trim() || null,
        lure_description: formData.lureDescription.trim() || null,
        
        // Color fields
        color_name: formData.colorName.trim(),
        color_description: formData.colorDescription.trim() || null,
        is_limited_edition: formData.isLimitedEdition,
        is_discontinued: formData.isDiscontinued,
        
        // Variant fields
        variant_name: formData.variantName.trim() || formData.colorName.trim(),
        variant_notes: formData.variantNotes.trim() || null,
        
        // Status
        status: 'pending'
      }
      
      console.log('Submission Data:', submissionData)
      
      // Insert into database
      const { data: insertedData, error: insertError } = await supabase
        .from('complete_lure_submissions')
        .insert([submissionData])
        .select()
        .single()
      
      if (insertError) {
        console.error('Insert error:', insertError)
        throw insertError
      }
      
      console.log('✅ Submission created:', insertedData)

      // Upload images if any
      if (submissionImages.length > 0) {
        try {
          await uploadImagesToSupabase(insertedData.submission_id)
          console.log('✅ Images uploaded successfully')
        } catch (imageError) {
          console.error('Image upload error:', imageError)
          // Don't fail the whole submission, just log the error
          console.warn('Submission succeeded but some images failed to upload')
        }
      }
      
      // Update submission counter in database
      try {
        const today = new Date().toISOString().split('T')[0]
        
        console.log('=== UPDATING SUBMISSION COUNTER ===')
        
        // First, try to get existing row for today
        const { data: existingRow, error: selectError } = await supabase
          .from('user_submission_limits')
          .select('complete_lure_count')
          .eq('user_id', user.id)
          .eq('submission_date', today)
          .maybeSingle()
        
        if (selectError && selectError.code !== 'PGRST116') {
          console.error('Error selecting existing row:', selectError)
        }
        
        if (existingRow) {
          // Row exists, increment the counter
          const newCount = existingRow.complete_lure_count + 1
          console.log('Incrementing counter from', existingRow.complete_lure_count, 'to', newCount)
          
          const { error: updateError } = await supabase
            .from('user_submission_limits')
            .update({ 
              complete_lure_count: newCount,
              updated_at: new Date().toISOString()
            })
            .eq('user_id', user.id)
            .eq('submission_date', today)
          
          if (updateError) {
            console.error('Error updating counter:', updateError)
          } else {
            console.log('✓ Counter updated successfully!')
          }
        } else {
          // No row for today, insert new one
          console.log('Creating new counter row')
          
          const { error: insertCounterError } = await supabase
            .from('user_submission_limits')
            .insert([{
              user_id: user.id,
              submission_date: today,
              complete_lure_count: 1
            }])
          
          if (insertCounterError) {
            console.error('Error inserting new counter row:', insertCounterError)
          } else {
            console.log('✓ New counter row created!')
          }
        }
      } catch (counterError) {
        console.error('Error updating submission counter:', counterError)
        // Don't fail the whole submission if counter update fails
      }
      
      // Success!
      setSuccess(true)
      setError('')
      
      // Refetch submission limits to show updated counter
      await fetchSubmissionLimits()
      
      // Reset form
      setFormData({
        existingBrandId: '',
        newBrandName: '',
        newBrandCountry: '',
        newBrandWebsite: '',
        newBrandDescription: '',
        lureName: '',
        lureTypeId: '',
        lureSubtypeId: '',
        lureWeight: '',
        lureLength: '',
        lureDepthRange: '',
        lureDescription: '',
        colorName: '',
        colorDescription: '',
        isLimitedEdition: false,
        isDiscontinued: false,
        variantName: '',
        variantNotes: ''
      })
      
      // Reset images
      setSubmissionImages([])
      setImageMetadata({ primaryIndex: 0, captions: {} })
      
      // Reset lure detection
      setExistingLure(null)
      setShowLureWarning(false)
      
      // Scroll to success banner at bottom
      setTimeout(() => {
        const successElement = document.querySelector('.contextual-feedback')
        if (successElement) {
          successElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
      
    } catch (error) {
      console.error('Submission error:', error)
      setError(error.message || 'Failed to submit. Please try again.')
      // Scroll to error banner
      setTimeout(() => {
        const errorElement = document.querySelector('.contextual-feedback')
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
    } finally {
      setSubmitting(false)
    }
  }

  // ============================================
  // RENDER
  // ============================================

  if (loading) {
    return (
      <div className="complete-lure-submission">
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: '#64748b'
        }}>
          Loading form...
        </div>
      </div>
    )
  }

  return (
    <div className="complete-lure-submission">
      <h1>Submit Complete Lure Package</h1>
      <p className="page-subtitle">
        Submit a complete lure entry (brand, lure, color, and variant) in one convenient form
      </p>

      {/* Upload Progress Banner - Keep at top since it's dynamic */}
      {uploadingImages && submissionImages.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid rgba(255,255,255,0.3)',
            borderTopColor: 'white',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <div>
            <strong style={{ display: 'block', fontSize: '1.1rem' }}>
              Uploading images...
            </strong>
            <p style={{ margin: '0.25rem 0 0 0', opacity: 0.9 }}>
              Please wait while we upload your {submissionImages.length} image(s)
            </p>
          </div>
        </div>
      )}

      {/* LURE DETECTION WARNING - Keep at top since it's contextual to typing */}
      {showLureWarning && existingLure && (
        <div style={{
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          padding: '1.5rem',
          borderRadius: '12px',
          marginBottom: '2rem',
          border: '2px solid #f59e0b'
        }}>
          <div style={{ display: 'flex', alignItems: 'start', gap: '1rem' }}>
            <span style={{ fontSize: '2rem' }}>⚠️</span>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#92400e' }}>
                Existing Lure Detected!
              </h3>
              <p style={{ margin: '0 0 0.5rem 0', color: '#78350f' }}>
                <strong>"{existingLure.name}"</strong> already exists for this brand.
              </p>
              <p style={{ margin: 0, color: '#78350f', fontSize: '0.95rem' }}>
                You'll be adding a new <strong>color variant</strong> to the existing lure. 
                Lure details (type, weight, length, depth) are locked from the existing entry.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Submission Counter - Keep at top since it's informational */}
      <div style={{
        background: 'white',
        padding: '1.5rem',
        borderRadius: '12px',
        marginBottom: '2rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        textAlign: 'center'
      }}>
        <div style={{ 
          display: 'inline-block',
          padding: '1rem 2rem',
          background: remainingSubmissions > 3 ? '#dbeafe' : remainingSubmissions > 0 ? '#fef3c7' : '#fee2e2',
          borderRadius: '8px'
        }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#1e3a8a' }}>
            {remainingSubmissions}
          </div>
          <div style={{ color: '#475569', fontSize: '0.9rem' }}>
            Submissions Remaining Today
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="admin-form">
        {/* ============================================
            SECTION 1: BRAND SELECTION
            ============================================ */}
        
        <div className="form-section">
          <h2>1️⃣ Brand Information</h2>
          
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              Do you want to use an existing brand or create a new one?
            </label>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                type="button"
                onClick={() => handleBrandTypeChange(true)}
                style={{
                  flex: 1,
                  padding: '1rem',
                  background: useExistingBrand ? '#2563eb' : '#f8fafc',
                  color: useExistingBrand ? 'white' : '#475569',
                  border: useExistingBrand ? '2px solid #2563eb' : '2px solid #e2e8f0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '1rem',
                  transition: 'all 0.3s'
                }}
              >
                Use Existing Brand
              </button>
              <button
                type="button"
                onClick={() => handleBrandTypeChange(false)}
                style={{
                  flex: 1,
                  padding: '1rem',
                  background: !useExistingBrand ? '#2563eb' : '#f8fafc',
                  color: !useExistingBrand ? 'white' : '#475569',
                  border: !useExistingBrand ? '2px solid #2563eb' : '2px solid #e2e8f0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '1rem',
                  transition: 'all 0.3s'
                }}
              >
                Create New Brand
              </button>
            </div>
          </div>

          {useExistingBrand ? (
            <div className="form-group">
              <label htmlFor="existingBrandId">Select Brand *</label>
              <select
                id="existingBrandId"
                name="existingBrandId"
                value={formData.existingBrandId}
                onChange={handleInputChange}
                className="form-select"
                required
              >
                <option value="">-- Choose a Brand --</option>
                {brands.map(brand => (
                  <option key={brand.brand_id} value={brand.brand_id}>
                    {brand.brand_name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="newBrandName">Brand Name *</label>
                <input
                  type="text"
                  id="newBrandName"
                  name="newBrandName"
                  value={formData.newBrandName}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="e.g., Megabass, Strike King, Lucky Craft"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="newBrandCountry">Country of Origin (Optional)</label>
                <input
                  type="text"
                  id="newBrandCountry"
                  name="newBrandCountry"
                  value={formData.newBrandCountry}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="e.g., Japan, USA, China"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="newBrandWebsite">Brand Website (Optional)</label>
                <input
                  type="url"
                  id="newBrandWebsite"
                  name="newBrandWebsite"
                  value={formData.newBrandWebsite}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="https://..."
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="newBrandDescription">Brand Description (Optional)</label>
                <textarea
                  id="newBrandDescription"
                  name="newBrandDescription"
                  value={formData.newBrandDescription}
                  onChange={handleInputChange}
                  className="form-textarea"
                  rows="4"
                  placeholder="Tell us about this brand..."
                ></textarea>
              </div>
            </>
          )}
        </div>

        {/* ============================================
            SECTION 2: LURE INFORMATION
            ============================================ */}
        
        <div className="form-section">
          <h2>2️⃣ Lure Information</h2>
          
          {existingLure && (
            <div style={{
              background: '#f0f9ff',
              border: '1px solid #bae6fd',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}>
              <p style={{ margin: '0 0 0.5rem 0', color: '#0c4a6e', fontWeight: '600' }}>
                ℹ️ Adding color to existing lure: "{existingLure.name}"
              </p>
              <p style={{ margin: 0, color: '#075985', fontSize: '0.9rem' }}>
                Lure details below are pre-filled and cannot be changed. You're only adding a new color variant.
              </p>
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="lureName">Lure Name *</label>
            <input
              type="text"
              id="lureName"
              name="lureName"
              value={formData.lureName}
              onChange={handleInputChange}
              className="form-input"
              placeholder="e.g., PopMax, Square Bill 1.5, Jerkbait 110"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="lureTypeId">Lure Type *</label>
            <select
              id="lureTypeId"
              name="lureTypeId"
              value={formData.lureTypeId}
              onChange={handleInputChange}
              className="form-select"
              required
              disabled={existingLure !== null}
            >
              <option value="">-- Choose a Type --</option>
              {types.map(type => (
                <option key={type.type_id} value={type.type_id}>
                  {type.type_name}
                </option>
              ))}
            </select>
            {existingLure && (
              <small className="field-hint" style={{ color: '#64748b' }}>
                Locked from existing lure
              </small>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="lureSubtypeId">Lure Subtype (Optional)</label>
            <select
              id="lureSubtypeId"
              name="lureSubtypeId"
              value={formData.lureSubtypeId}
              onChange={handleInputChange}
              className="form-select"
              disabled={existingLure !== null}
            >
              <option value="">-- Choose a Subtype (Optional) --</option>
              {subtypes.map(subtype => (
                <option key={subtype.subtype_id} value={subtype.subtype_id}>
                  {subtype.subtype_name}
                </option>
              ))}
            </select>
            {existingLure && (
              <small className="field-hint" style={{ color: '#64748b' }}>
                Locked from existing lure
              </small>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="lureWeight">Weight (Optional)</label>
            <input
              type="text"
              id="lureWeight"
              name="lureWeight"
              value={formData.lureWeight}
              onChange={handleInputChange}
              className="form-input"
              placeholder="e.g., 1/2 oz, 3/8 oz"
              disabled={existingLure !== null}
            />
            <small className="field-hint">
              {existingLure ? 'Locked from existing lure' : 'Include units (oz, g, etc.)'}
            </small>
          </div>
          
          <div className="form-group">
            <label htmlFor="lureLength">Length (Optional)</label>
            <input
              type="text"
              id="lureLength"
              name="lureLength"
              value={formData.lureLength}
              onChange={handleInputChange}
              className="form-input"
              placeholder="e.g., 3.5 in, 4 inches"
              disabled={existingLure !== null}
            />
            <small className="field-hint">
              {existingLure ? 'Locked from existing lure' : 'Include units (in, cm, etc.)'}
            </small>
          </div>
          
          <div className="form-group">
            <label htmlFor="lureDepthRange">Depth Range (Optional)</label>
            <input
              type="text"
              id="lureDepthRange"
              name="lureDepthRange"
              value={formData.lureDepthRange}
              onChange={handleInputChange}
              className="form-input"
              placeholder="e.g., 3-5 feet, 1-2 meters, Topwater"
              disabled={existingLure !== null}
            />
            {existingLure && (
              <small className="field-hint" style={{ color: '#64748b' }}>
                Locked from existing lure
              </small>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="lureDescription">Lure Description (Optional)</label>
            <textarea
              id="lureDescription"
              name="lureDescription"
              value={formData.lureDescription}
              onChange={handleInputChange}
              className="form-textarea"
              rows="4"
              placeholder="Describe the lure's action, features, target species..."
              disabled={existingLure !== null}
            ></textarea>
            {existingLure && (
              <small className="field-hint" style={{ color: '#64748b' }}>
                Locked from existing lure
              </small>
            )}
          </div>
        </div>

        {/* ============================================
            SECTION 3: COLOR INFORMATION
            ============================================ */}
        
        <div className="form-section">
          <h2>3️⃣ Color Information</h2>
          
          <div className="form-group">
            <label htmlFor="colorName">Color Name *</label>
            <input
              type="text"
              id="colorName"
              name="colorName"
              value={formData.colorName}
              onChange={handleInputChange}
              className="form-input"
              placeholder="e.g., Sexy Shad, Chartreuse, Ghost Minnow"
              required
            />
            <small className="field-hint">
              Use the official color name from the manufacturer
            </small>
          </div>
          
          <div className="form-group">
            <label htmlFor="colorDescription">Color Description (Optional)</label>
            <textarea
              id="colorDescription"
              name="colorDescription"
              value={formData.colorDescription}
              onChange={handleInputChange}
              className="form-textarea"
              rows="3"
              placeholder="Describe the color pattern, when it was released, etc..."
            ></textarea>
          </div>
          
          <div className="form-checkboxes">
            <label className="checkbox-label-admin">
              <input
                type="checkbox"
                name="isLimitedEdition"
                checked={formData.isLimitedEdition}
                onChange={handleInputChange}
              />
              <span>Limited Edition Color</span>
            </label>
            
            <label className="checkbox-label-admin">
              <input
                type="checkbox"
                name="isDiscontinued"
                checked={formData.isDiscontinued}
                onChange={handleInputChange}
              />
              <span>Discontinued Color</span>
            </label>
          </div>
        </div>

        {/* ============================================
            SECTION 4: VARIANT INFORMATION
            ============================================ */}
        
        <div className="form-section">
          <h2>4️⃣ Variant Details (Optional)</h2>
          
          <div className="form-group">
            <label htmlFor="variantName">Variant Name (Optional)</label>
            <input
              type="text"
              id="variantName"
              name="variantName"
              value={formData.variantName}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Leave blank to use the color name"
            />
            <small className="field-hint">
              Usually the variant name is the same as the color name. Only fill this if different.
            </small>
          </div>
          
          <div className="form-group">
            <label htmlFor="variantNotes">Variant Notes (Optional)</label>
            <textarea
              id="variantNotes"
              name="variantNotes"
              value={formData.variantNotes}
              onChange={handleInputChange}
              className="form-textarea"
              rows="3"
              placeholder="Any additional notes about this specific variant..."
            ></textarea>
          </div>
        </div>

        {/* ============================================
            SECTION 5: PRODUCT IMAGES
            ============================================ */}
        
        <div className="form-section">
          <h2>5️⃣ Product Images (Optional)</h2>
          <p className="section-description">
            Upload images of this lure to help our team verify and approve your submission. 
            High-quality images improve approval chances!
          </p>
          
          <ImageUploader
            maxImages={5}
            onImagesChange={handleSubmissionImagesChange}
            existingImages={[]}
          />
        </div>

        {/* ============================================
            CONTEXTUAL FEEDBACK (SUCCESS/ERROR BANNERS)
            ============================================ */}
        
        <div className="contextual-feedback">
          {/* Success Message - Positioned right before submit buttons */}
          {success && (
            <div className="contextual-banner contextual-banner-success">
              <div className="contextual-banner-content">
                <div className="contextual-banner-icon">✅</div>
                <div className="contextual-banner-text">
                  <h3>Submission Successful!</h3>
                  <p>
                    Your complete lure package has been submitted for review.
                    {submissionImages.length > 0 && ` Including ${submissionImages.length} image(s)!`}
                  </p>
                  <p>Our admin team will process it shortly.</p>
                  <p className="contextual-banner-meta">
                    You have {remainingSubmissions} submissions remaining today.
                  </p>
                  <div className="contextual-banner-actions">
                    <button 
                      type="button"
                      onClick={() => {
                        setSuccess(false)
                        window.scrollTo({ top: 0, behavior: 'smooth' })
                      }}
                      className="btn-primary"
                    >
                      Submit Another Lure
                    </button>
                    <button 
                      type="button"
                      onClick={() => navigate('/browse')}
                      className="btn-secondary"
                    >
                      Back to Browse
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Message - Positioned right before submit buttons */}
          {error && (
            <div className="contextual-banner contextual-banner-error">
              <div className="contextual-banner-content">
                <div className="contextual-banner-icon">⚠️</div>
                <div className="contextual-banner-text">
                  <h3>Error</h3>
                  <p>{error}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ============================================
            SUBMIT BUTTONS
            ============================================ */}
        
        <div className="form-actions">
          <button 
            type="button" 
            onClick={() => navigate('/browse')}
            className="btn-secondary"
            disabled={submitting || uploadingImages}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn-primary"
            disabled={submitting || uploadingImages || remainingSubmissions <= 0}
          >
            {uploadingImages 
              ? `Uploading images...` 
              : submitting 
                ? 'Submitting...' 
                : 'Submit Complete Package'
            }
          </button>
        </div>
      </form>
    </div>
  )
}

export default SubmitCompleteLurePackage